import React, { Component } from 'react';

const buildCdn = (src, { w, h, q, fit, fm } = {}) => {
  const p = new URLSearchParams({ url: src });
  if (w) p.set('w', String(w));
  if (h) p.set('h', String(h));
  if (q) p.set('q', String(q));
  if (fit) p.set('fit', fit);
  if (fm) p.set('fm', fm);
  return `/.netlify/images?${p.toString()}`;
};

const isLocal = (src) => typeof src === 'string' && src.startsWith('/');

class StableImage extends Component {
  constructor(props) {
    super(props);
    this.state = {
      broken: false,
      useCdn: isLocal(props.src),     // try CDN for local assets first
      attempt: 0,
    };
    this.onError = this.onError.bind(this);
  }

  static defaultProps = {
    alt: '',
    className: '',
    retries: 1,
    delay: 300,
    widths: [512, 768, 1024, 1536],
    q: 70,
    fit: undefined, // 'cover' | 'contain' | 'inside' | 'outside'
    fm: undefined, // 'avif' | 'webp' | 'png' | 'jpg'
    sizes: '(max-width: 768px) 90vw, 768px',
    disableCdn: false,
    fallbackSrc: null, // optional: show this image if completely broken
    priority: false,   // if true -> eager load + fetchpriority=high
  };

  componentDidUpdate(prevProps) {
    // If src changes, reset state
    if (prevProps.src !== this.props.src) {
      this.setState({
        broken: false,
        useCdn: isLocal(this.props.src) && !this.props.disableCdn,
        attempt: 0,
      });
    }
  }

  onError() {
    const { retries, delay } = this.props;
    const { attempt, useCdn } = this.state;

    // 1) If CDN failed, fall back to raw file once
    if (useCdn) {
      this.setState({ useCdn: false, attempt: attempt + 1 });
      return;
    }

    // 2) Retry logic (toggle "broken" briefly to force re-render)
    if (attempt < retries) {
      this.setState({ broken: true, attempt: attempt + 1 });
      setTimeout(() => this.setState({ broken: false }), delay);
    } else {
      // 3) Give up: either show fallback or hide
      this.setState({ broken: true });
    }
  }

  render() {
    const {
      src,
      alt,
      className,
      widths,
      q,
      fit,
      fm,
      sizes,
      disableCdn,
      fallbackSrc,
      priority,
      ...rest
    } = this.props;
    const { broken, useCdn } = this.state;

    if (broken) {
      if (fallbackSrc) {
        // show a provided fallback instead of null
        return (
          <img
            src={fallbackSrc}
            alt={alt}
            className={className + ' no-select'}
            loading={priority ? 'eager' : 'lazy'}
            fetchpriority={priority ? 'high' : 'auto'}
            decoding="async"
            {...rest}
          />
        );
      }
      return null;
    }

    const shouldCdn = isLocal(src) && !disableCdn && useCdn;
    const defaultW = widths[Math.min(1, widths.length - 1)] || 768;

    const imgSrc = shouldCdn
      ? buildCdn(src, { w: defaultW, q, fit, fm })
      : src;

    const srcSet = shouldCdn
      ? widths.map((w) => `${buildCdn(src, { w, q, fit, fm })} ${w}w`).join(', ')
      : undefined;

    return (
      <img
        src={imgSrc}
        srcSet={srcSet}
        sizes={srcSet ? sizes : undefined}
        alt={alt}
        className={className + ' no-select'}
        onError={this.onError}
        loading={priority ? 'eager' : 'lazy'}
        fetchpriority={priority ? 'high' : 'auto'}
        decoding="async"
        {...rest}
      />
    );
  }
}

export default StableImage;